<script setup lang="ts">
import { ErrorMessage, useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { object, string } from 'zod'
import logo from '~/assets/img/scuola-per-ricchi.svg'

definePageMeta({
  layout: false,
  auth: false,
})

const route = useRoute()

const { $trpc } = useNuxtApp()

const form = useForm({
  initialValues: {
    email: import.meta.env.DEV ? 'user@katanga.app' : '',
  },
  validationSchema: toTypedSchema(
    object({
      email: string().email(),
    }),
  ),
})

if (typeof route.query.email === 'string') {
  form.setValues({ email: route.query.email })
}

const { handleSubmit, isSubmitting, defineField } = form

const handleError = useFormErrorHandler(form)

const [email, emailAttrs] = defineField('email')

const turnstileElement = ref<HTMLDivElement>()
const turnstile = useTurnstile(turnstileElement)

const success = ref(false)

const onSubmit = handleSubmit(async (values) => {
  if (success.value) {
    return
  }

  try {
    const token = await turnstile.challenge()

    await $trpc.auth.requestPasswordReset.mutate({
      email: values.email,
      token,
    })
    success.value = true
  }
  catch (e) {
    handleError(e)
  }
})
</script>

<template>
  <div class="flex-col justify-center flex-1 min-h-full py-12 sm:px-6 lg:px-8">
    <div class="absolute top-4 right-4">
      <ToggleDarkButton />
    </div>

    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="p-3 mx-auto w-36 aspect-1/1 rounded-xl bg-default-blue">
        <img class="w-full h-full mx-auto pointer-events-none select-none" draggable="false" :src="logo" alt="SPR">
      </div>
      <h2 class="mt-6 text-2xl font-bold leading-9 tracking-tight text-center">
        Reimposta la tua password
      </h2>
      <p class="mt-4 text-sm font-medium leading-6 text-center">
        Per reimpostare la password, inserisci l'indirizzo email che utilizzi per accedere.
      </p>
    </div>

    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="px-6 py-12 sm:rounded-lg sm:px-12">
        <transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="transform opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="duration-200 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="transform opacity-0"
        >
          <div v-if="success" class="p-4 mb-4 rounded-md bg-green-50">
            <div class="flex">
              <div class="ml-3">
                <h3 class="text-sm font-medium text-green-800">
                  <!-- Se l'indirizzo email <email@example.com> è associato ad un account, a breve riceverai un'e-mail per reimpostare la password. Se non hai ricevuto la mail, puoi creare un nuovo account. -->
                  A breve riceverai un'e-mail per reimpostare la password.
                </h3>
                <div class="mt-2 text-sm text-green-700">
                  <p>Assicurati di controllare la cartella spam.</p>
                </div>
              </div>
            </div>
          </div>
        </transition>

        <form class="space-y-6" action="/" method="POST" @submit="onSubmit">
          <div>
            <label for="email" class="block text-sm font-medium leading-6">Email</label>
            <div class="mt-2">
              <input
                id="email"
                v-model="email"
                v-bind="emailAttrs"
                :disabled="isSubmitting"
                :class="{ 'animate-pulse disabled:cursor-wait': isSubmitting }"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="block w-full px-3 py-2 border-0 rounded-md outline-none bg-neutral-200 dark:bg-neutral-700 ring-1 ring-inset ring-transparent focus:ring-2 focus:ring-inset focus:ring-neutral-600 sm:text-sm sm:leading-6"
              >

              <ErrorMessage class="text-red-500" name="email" />
            </div>
          </div>

          <div>
            <button
              :disabled="isSubmitting"
              :class="{ 'animate-pulse disabled:cursor-wait': isSubmitting }"
              type="submit"
              class="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              <template v-if="isSubmitting">
                <svg
                  class="w-5 h-5 mr-3 -ml-1 text-white animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </template>
              Invia il link di ripristino
            </button>
          </div>
        </form>

        <div ref="turnstileElement" class="my-6" />

        <div class="flex items-center justify-center mt-12">
          <p class="text-base font-bold">
            Non importa!
            <NuxtLink to="/login" class="px-1 py-1 text-base font-bold underline rounded-md focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
              Riportami al Login
            </NuxtLink>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
